import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  input,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BLACK, DocumentAnnotation, YELLOW_HIGHLIGHT } from 'models';
import { NGXLogger } from 'ngx-logger';
import { BaseMenuComponent } from 'src/app/common/base-menu-component';
import { DocumentAnnotationService } from 'src/app/services/document-annotation.service';
import { SupportedFeaturesService } from 'src/app/services/supported-features.service';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { StampsQuery } from 'src/app/state/stamps/stamps.query';
import { AnnotationPropertiesComponent } from '../annotation-properties/annotation-properties.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-annotation-menu',
  templateUrl: './annotation-menu.component.html',
  styleUrl: './annotation-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AnnotationMenuComponent
  extends BaseMenuComponent
  implements OnInit
{
  /** Determines if we should display the annotation menu based on API version. */
  annotationsSupported = this.supportedFeatures.versionSupports(
    this.supportedFeatures.annotationsDrawing
  );

  /** Observable array of the user's custom stamps. */
  customStamps$ = this.appQuery.customStamps$;
  /**
   * A boolean or function that returns a boolean indicating if the menu should be disabled.
   */
  disableMenuInput = input<boolean | (() => boolean)>(false, {
    alias: 'disableMenu',
  });
  /**
   * Whether the menu should be disabled.
   */
  disableMenu = computed(() => {
    const disableMenuInput = this.disableMenuInput();
    return typeof disableMenuInput === 'function'
      ? disableMenuInput()
      : disableMenuInput;
  });
  /** Observable array of stamps. */
  stamps$ = this.stampsQuery.stamps$.pipe(untilDestroyed(this));
  /** Text that should be displayed for the toggle edit button. */
  toggleEditText = computed(() => {
    const translationKey = this.annotationService.pointerEventsEnabled()
      ? 'DISABLE_EDIT'
      : 'ENABLE_EDIT_MENU_ITEM';

    return this.transloco.translate(translationKey);
  });

  constructor(
    private logger: NGXLogger,
    private supportedFeatures: SupportedFeaturesService,
    private annotationService: DocumentAnnotationService,
    private appQuery: ApplicationQuery,
    private stampsQuery: StampsQuery,
    private transloco: TranslocoService,
    public dialog: MatDialog
  ) {
    super();
  }
  ngOnInit(): void {
    // Subscribe to annotation properties.
    this.annotationService.annotationProperties$
      .pipe(untilDestroyed(this))
      .subscribe((data: DocumentAnnotation | undefined) => {
        // Don't execute if nothing is passed.
        if (!data) return;
        const dialog = this.dialog.open(AnnotationPropertiesComponent, {
          width: 'auto',
          data: data,
          disableClose: true,
        });
        dialog.afterClosed().subscribe((result: DocumentAnnotation) => {
          if (!result) {
            this.logger.debug('Annotation properties dialog was cancelled.');
            return;
          }
          this.annotationService.updateAnnotation(result);
          this.annotationService.setDirtyStatus(true);
        });
      });
  }

  /**
   * Enables highlight annotation drawing.
   *
   * @param event - Mouse Event.
   */
  onClickCreateHighlightAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawRectangle(event, YELLOW_HIGHLIGHT, true);
  }

  /**
   * Enable redaction annotation drawing.
   *
   * @param event - Mouse Event.
   */
  onClickCreateRedactionAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawRectangle(event, BLACK, false);
  }

  /**
   * Enable image annotation drawing.
   *
   * @param event - Mouse Event.
   */
  onClickCreateImageAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawImage(event);
  }

  /**
   * Enable line annotation drawing.
   *
   * @param event - Mouse Event.
   */
  onClickCreateFreehandAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawFreehand(event);
  }

  /**
   * Enable note annotation drawing.
   *
   * @param event - Mouse Event.
   */
  onClickCreateNoteAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawNote(event);
  }

  /**
   * Enable stamp annotation drawing.
   *
   * @param event - Mouse Event.
   * @param text - Stamp text.
   */
  onClickCreateStampAnnotation(event: MouseEvent, text: string) {
    this.annotationService.enableDrawStamp(event, text);
  }

  /**
   * Enable line annotation drawing.
   *
   * @param event - Mouse Event.
   */
  onClickCreateLineAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawLine(event);
  }

  /**
   * Enable ellipse annotation drawing.
   *
   * @param event - Mouse Event.
   */
  onClickCreateEllipseAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawEllipse(event);
  }

  onClickToggleEdit() {
    this.annotationService.togglePointerEvents();
  }

  /**
   * Opens the annotation properties dialog for the passed annotation.
   *
   * @param annotation - Annotation.
   */
  openAnnotationProperties(annotation: DocumentAnnotation) {
    this.dialog.open(AnnotationPropertiesComponent, {
      data: {
        annotation: annotation,
      },
    });
  }
}
